/**
 * Cruise cabin pricing - thin adapter over the in-house hotel-pricing engine.
 *
 * A cruise cabin prices exactly like a hotel room (base occupancy + per-pax
 * supplements, or a fixed whole-cabin cost), so this module does NOT
 * reimplement pricing logic. It only adapts the cruise domain's rate shape
 * (`CruiseCabinRate`-like object) onto `HotelRateInput` and re-exports the
 * hotel-pricing functions under cruise-friendly names.
 */

import {
  priceRoomNight,
  priceRoomStay,
  type HotelRateInput,
  type RoomOccupancy,
} from './hotel-pricing';

/** Map a cruise cabin rate (+ cabin capacity) onto the shared hotel rate input. */
export function cabinRateToRateInput(
  rate: {
    fixedCabinCost: number;
    adultPrice: number;
    childWithBedPrice: number;
    childWithoutBedPrice: number;
    infantPrice: number;
    extraAdultPrice: number;
    baseOccupancy?: number | null;
    occupancyPaxCount?: number | null;
  },
  cabinMaxOccupancy: number,
  meal?: { perAdult: number; perChild: number },
): HotelRateInput {
  return {
    fixedRoomCost: rate.fixedCabinCost,
    adultPrice: rate.adultPrice,
    childWithBedPrice: rate.childWithBedPrice,
    childWithoutBedPrice: rate.childWithoutBedPrice,
    infantPrice: rate.infantPrice,
    extraAdultPrice: rate.extraAdultPrice,
    mealPerAdult: meal?.perAdult ?? 0,
    mealPerChild: meal?.perChild ?? 0,
    baseAdults: rate.baseOccupancy ?? rate.occupancyPaxCount ?? 2,
    // Match the hotel path (auto-search `toRate`): in whole-cabin/FIXED buyout
    // mode the first child is included in the base, same as a hotel room.
    baseChildrenIncluded: 1,
    maxOccupancy: cabinMaxOccupancy,
  };
}

export const priceCabinNight = priceRoomNight;
export const priceCabinStay = priceRoomStay;
export type CabinOccupancy = RoomOccupancy;
